import { createTheme, alpha } from '@mui/material/styles';

// Define the color palette
export const colors = {
  primaryBg: '#121212',
  secondaryBg: '#1E1E1E',
  tertiaryBg: '#2D2D2D',
  primaryRed: '#E31B23',
  darkRed: '#C41017',
  lightRed: '#FF3B42',
  blueAccent: '#2874A6',
  blueAccentLight: '#3498DB',
  successGreen: '#27AE60',
  warningAmber: '#F39C12',
  whiteText: '#FFFFFF',
  lightGray: '#CCCCCC',
  mediumGray: '#888888',
  dividerGray: '#333333',
};

// Create theme function
const createExileGuardTheme = () => {
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: colors.primaryRed,
        dark: colors.darkRed,
        light: colors.lightRed,
        contrastText: colors.whiteText,
      },
      secondary: {
        main: colors.blueAccent,
        light: colors.blueAccentLight,
        contrastText: colors.whiteText,
      },
      success: {
        main: colors.successGreen,
      },
      warning: {
        main: colors.warningAmber,
      },
      error: {
        main: colors.lightRed,
        dark: colors.primaryRed,
      },
      background: {
        default: colors.primaryBg,
        paper: colors.secondaryBg,
      },
      text: {
        primary: colors.whiteText,
        secondary: colors.lightGray,
        disabled: colors.mediumGray,
      },
      divider: colors.dividerGray,
      // Custom colors for specific components
      action: {
        active: alpha(colors.whiteText, 0.7),
        hover: alpha(colors.primaryRed, 0.12),
        selected: alpha(colors.primaryRed, 0.16),
        disabled: alpha(colors.mediumGray, 0.3),
        disabledBackground: alpha(colors.mediumGray, 0.12),
      },
    },

    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.3,
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.4,
      },
      h5: {
        fontWeight: 500,
        fontSize: '1.125rem',
        lineHeight: 1.4,
      },
      h6: {
        fontWeight: 500,
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.57,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.43,
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'none', // Override MUI's default uppercase
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.66,
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 1,
      },
    },

    // Custom component overrides
    components: {
      // Container with consistent spacing
      MuiContainer: {
        defaultProps: {
          maxWidth: 'xl' as const,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(colors.primaryBg, 0.8),
            backdropFilter: 'blur(8px)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.primaryBg,
            borderRight: `1px solid ${colors.dividerGray}`,
          },
        },
      },
      // Enhanced cards with AlertsPage-style animations
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: colors.secondaryBg,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            borderRadius: 12,
            transition: 'all 0.3s ease-out',
            overflow: 'visible',
            position: 'relative',
            '&:hover': {
              boxShadow: '0 16px 32px rgba(227, 27, 35, 0.2)',
              borderColor: colors.primaryRed,
            },
          },
        },
      },
      // Enhanced Paper component
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: colors.secondaryBg,
            borderRadius: 8,
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.4)',
            },
          },
          elevation1: {
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          },
          elevation2: {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          },
          elevation3: {
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: '16px',
            borderBottom: `1px solid ${colors.dividerGray}`,
          },
          title: {
            fontSize: '1.125rem',
            fontWeight: 500,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '16px',
            '&:last-child': {
              paddingBottom: '16px',
            },
          },
        },
      },
      // Enhanced buttons with better styling
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 20px',
            transition: 'all 0.2s ease',
          },
          contained: {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            '&:hover': {
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.3)',
            },
            '&:active': {
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${colors.primaryRed} 0%, ${colors.lightRed} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${colors.darkRed} 0%, ${colors.primaryRed} 100%)`,
            },
          },
          outlined: {
            borderWidth: '2px',
            borderColor: alpha(colors.primaryRed, 0.5),
            '&:hover': {
              borderWidth: '2px',
              borderColor: colors.primaryRed,
              backgroundColor: alpha(colors.primaryRed, 0.1),
            },
            '&.Mui-disabled': {
              borderWidth: '2px',
            },
          },
          text: {
            '&:hover': {
              backgroundColor: alpha(colors.primaryRed, 0.1),
            },
          },
        },
      },
      // Enhanced IconButton
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(colors.primaryRed, 0.15),
              color: colors.primaryRed,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${colors.dividerGray}`,
          },
          head: {
            fontWeight: 600,
            backgroundColor: alpha(colors.tertiaryBg, 0.6),
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: colors.primaryRed,
            height: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            '&.Mui-selected': {
              color: colors.whiteText,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.primaryBg,
            color: colors.whiteText,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: colors.primaryBg,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors.dividerGray,
              borderRadius: 4,
              '&:hover': {
                backgroundColor: colors.mediumGray,
              },
            },
          },
          // Global animations for consistency
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(10px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 1,
            },
            '50%': {
              opacity: 0.6,
            },
          },
          '@keyframes slideIn': {
            '0%': {
              opacity: 0,
              transform: 'translateX(-20px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateX(0)',
            },
          },
          // Utility classes for consistent animations
          '.fade-in': {
            animation: 'fadeIn 0.3s ease-out',
          },
          '.slide-in': {
            animation: 'slideIn 0.3s ease-out',
          },
          '.pulse': {
            animation: 'pulse 2s infinite',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            '&.Mui-selected': {
              backgroundColor: alpha(colors.primaryRed, 0.16),
              '&:hover': {
                backgroundColor: alpha(colors.primaryRed, 0.24),
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '20%',
                height: '60%',
                width: 3,
                backgroundColor: colors.primaryRed,
                borderRadius: '0 2px 2px 0',
              },
            },
          },
        },
      },
      // Enhanced chips with hover effects
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            fontWeight: 600,
            fontSize: '0.75rem',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
              backgroundColor: alpha(colors.primaryRed, 0.2),
            },
          },
          filled: {
            backgroundColor: alpha(colors.primaryRed, 0.15),
            color: colors.primaryRed,
            '&:hover': {
              backgroundColor: alpha(colors.primaryRed, 0.25),
            },
          },
          outlined: {
            borderColor: alpha(colors.primaryRed, 0.5),
            '&:hover': {
              borderColor: colors.primaryRed,
              backgroundColor: alpha(colors.primaryRed, 0.1),
            },
          },
        },
      },
      // Enhanced TextField
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              },
              '&.Mui-focused': {
                boxShadow: `0 4px 12px ${alpha(colors.primaryRed, 0.3)}`,
              },
            },
          },
        },
      },
      // Enhanced CircularProgress
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            animationDuration: '1.2s',
          },
          colorPrimary: {
            color: colors.primaryRed,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          standardError: {
            backgroundColor: alpha(colors.lightRed, 0.1),
            color: colors.lightRed,
          },
          standardWarning: {
            backgroundColor: alpha(colors.warningAmber, 0.1),
            color: colors.warningAmber,
          },
          standardInfo: {
            backgroundColor: alpha(colors.blueAccentLight, 0.1),
            color: colors.blueAccentLight,
          },
          standardSuccess: {
            backgroundColor: alpha(colors.successGreen, 0.1),
            color: colors.successGreen,
          },
        },
      },
    },

    // Spacing and breakpoints
    spacing: 8, // Base spacing unit of 8px
    shape: {
      borderRadius: 4,
    },
    // MUI's default breakpoints
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  });
};

export default createExileGuardTheme;
